/**
 * iOS safe-area helpers.
 * With apple-mobile-web-app-status-bar-style=default the system usually offsets
 * the webview. We only force --sat-fallback when env() is ~0 AND the layout
 * still looks underlapped (legacy / MAX / odd iOS builds).
 */
export function ensureIosSafeAreaFallback(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const ios = /iP(hone|od|ad)/.test(navigator.userAgent);
  if (!ios) return;

  document.documentElement.classList.add("ios");

  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    Boolean(nav.standalone);
  if (standalone) document.documentElement.classList.add("ios-standalone");

  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)";
  document.documentElement.appendChild(probe);
  const cs = getComputedStyle(probe);
  const top = parseFloat(cs.paddingTop) || 0;
  const bottom = parseFloat(cs.paddingBottom) || 0;
  probe.remove();

  // Only force top fallback when env is clearly broken (0) in a covered viewport.
  // status-bar-style=default usually means we must NOT add another 47px.
  const meta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  const translucent = (meta?.getAttribute("content") || "").includes("translucent");
  if (translucent && top < 20) {
    document.documentElement.style.setProperty("--sat-fallback", "47px");
    document.documentElement.classList.add("ios-sat-fallback");
  }
  if ((standalone || translucent) && bottom < 1) {
    document.documentElement.style.setProperty("--sab-fallback", "34px");
  }
}

/** Header class — shell owns notch inset; this is only light padding. */
export const SAFE_TOP_PAD = "safe-top-pad";
